import React, { useState, useEffect } from 'react';
import { 
  LifeBuoy, Send, MessageCircle, Info, Clock, CheckCircle2, 
  FileText, ChevronDown, ChevronUp, HelpCircle, Mail, 
  Phone, MessageSquare, User, Tag, Calendar, ArrowRight,
  AlertCircle, CheckCircle, XCircle, Clock as ClockIcon,
  Paperclip, Image, Download, ExternalLink, Star, Headphones,
  Zap, Shield, Award, Users, TrendingUp
} from 'lucide-react';
import { toast } from 'react-toastify';

import axios from 'axios';

const FAQS = [
  {
    q: "When will I get my payment?",
    a: "Payments are moved to your 'Available Balance' exactly 7 days after an order is marked as 'Delivered'. You can then withdraw it instantly to your bank account.",
    category: "Payment"
  },
  {
    q: "How do I handle a return?",
    a: "If a customer requests a return, you will receive a notification. You have 48 hours to respond with a resolution before admin steps in. We recommend accepting returns for damaged plants.",
    category: "Orders"
  },
  {
    q: "Can I edit an order after it's placed?",
    a: "No, sellers cannot edit orders to prevent fraud. You can only update the status to Shipped or Delivered. Contact support for any urgent changes.",
    category: "Orders"
  },
  {
    q: "How to increase my product visibility?",
    a: "Optimize your product listings with high-quality images, detailed descriptions, and competitive pricing. Responding to reviews quickly also helps boost your seller rating.",
    category: "Marketing"
  }
];

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState('');
  const [issueType, setIssueType] = useState('Payment');
  const [description, setDescription] = useState('');
  const [activeFaq, setActiveFaq] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.get('/api/seller/support', { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setTickets(res.data.tickets);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const payload = {
        subject,
        category: issueType,
        message: description,
        priority: 'medium'
      };
      const res = await axios.post('/api/seller/support', payload, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        setTickets([res.data.ticket, ...tickets]);
        setSubject('');
        setDescription('');
        setIssueType('Payment');
        setSelectedFile(null);
        toast.success("Support ticket submitted successfully!");
        setActiveTab('tickets');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to submit ticket');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open': 
        return <span className="status-badge open"><ClockIcon size={12} /> Open</span>;
      case 'in_progress': 
        return <span className="status-badge progress"><AlertCircle size={12} /> In Progress</span>;
      case 'resolved': 
        return <span className="status-badge resolved"><CheckCircle size={12} /> Resolved</span>;
      default: 
        return <span className="status-badge">{status}</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case 'high': 
        return <span className="priority-badge high">High Priority</span>;
      case 'medium': 
        return <span className="priority-badge medium">Medium Priority</span>;
      default: 
        return <span className="priority-badge low">Low Priority</span>;
    }
  };

  const getTicketsByStatus = (status) => {
    return tickets.filter(t => t.status === status).length;
  };

  const QuickAction = ({ icon: Icon, title, description, onClick, color }) => (
    <div className="quick-action" onClick={onClick}>
      <div className="quick-action-icon" style={{ backgroundColor: `${color}15` }}>
        <Icon size={24} color={color} />
      </div>
      <div className="quick-action-info">
        <h6>{title}</h6>
        <p>{description}</p>
      </div>
      <ArrowRight size={16} className="quick-action-arrow" />
    </div>
  );

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="stats-card-support">
      <div className="stats-icon" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <h3>{value}</h3>
        <p>{title}</p>
      </div>
    </div>
  );

  return (
    <div className="support-page">
      {/* Header */}
      <div className="support-header">
        <div className="header-left">
          <h1 className="page-title-support">Support & Help Center</h1>
          <p className="page-subtitle-support">Get help with orders, payments, or account issues</p>
        </div>
        <div className="header-contact">
          <div className="contact-item">
            <Mail size={16} />
            <span>support@paudhawala.com</span>
          </div>
          <div className="contact-item">
            <Phone size={16} />
            <span>+91 1800-123-4567</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-support">
        <QuickAction 
          icon={HelpCircle} 
          title="Raise a Ticket" 
          description="Get help from support team"
          onClick={() => setActiveTab('tickets')}
          color="#10b981"
        />
        <QuickAction 
          icon={FileText} 
          title="FAQs & Guides" 
          description="Browse common questions"
          onClick={() => setActiveTab('faq')}
          color="#3b82f6"
        />
        <QuickAction 
          icon={Headphones} 
          title="Live Chat" 
          description="Chat with support agent"
          onClick={() => toast.info('Live chat coming soon')}
          color="#8b5cf6"
        />
        <QuickAction 
          icon={MessageCircle} 
          title="Community" 
          description="Connect with other sellers"
          onClick={() => toast.info('Community forum coming soon')}
          color="#f59e0b"
        />
      </div>

      {/* Stats */}
      <div className="stats-support">
        <StatCard title="Open Tickets" value={getTicketsByStatus('open')} icon={AlertCircle} color="#f59e0b" />
        <StatCard title="In Progress" value={getTicketsByStatus('in_progress')} icon={ClockIcon} color="#3b82f6" />
        <StatCard title="Resolved" value={getTicketsByStatus('resolved')} icon={CheckCircle} color="#10b981" />
        <StatCard title="Total Tickets" value={tickets.length} icon={MessageCircle} color="#8b5cf6" />
      </div>

      {/* Main Content Tabs */}
      <div className="support-tabs">
        <button className={`tab-support ${activeTab === 'tickets' ? 'active' : ''}`} onClick={() => setActiveTab('tickets')}>
          <MessageCircle size={16} />
          My Tickets
        </button>
        <button className={`tab-support ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => setActiveTab('faq')}>
          <HelpCircle size={16} />
          FAQs
        </button>
        <button className={`tab-support ${activeTab === 'new' ? 'active' : ''}`} onClick={() => setActiveTab('new')}>
          <Send size={16} />
          New Ticket
        </button>
      </div>

      {/* Tickets List */}
      {activeTab === 'tickets' && (
        <div className="tickets-container">
          {tickets.length === 0 ? (
            <div className="empty-state-support">
              <div className="empty-icon">
                <MessageCircle size={64} />
              </div>
              <h3>No support tickets</h3>
              <p>You haven't raised any issues yet. Need help? Create a new ticket.</p>
              <button className="btn-primary-support" onClick={() => setActiveTab('new')}>
                Create New Ticket
              </button>
            </div>
          ) : (
            <div className="tickets-list">
              {tickets.map(ticket => (
                <div key={ticket.id} className="ticket-card">
                  <div className="ticket-header">
                    <div className="ticket-id">
                      <span className="id-label">#{ticket._id.substring(ticket._id.length - 6).toUpperCase()}</span>
                      {getPriorityBadge(ticket.priority || 'low')}
                    </div>
                    <div className="ticket-status">
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                  <div className="ticket-body">
                    <h6 className="ticket-subject">{ticket.subject}</h6>
                    <p className="ticket-description">{ticket.messages?.[0]?.message || ''}</p>
                    <div className="ticket-meta">
                      <span className="meta-item">
                        <Tag size={12} />
                        {ticket.category}
                      </span>
                      <span className="meta-item">
                        <Calendar size={12} />
                        {new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div className="ticket-footer">
                    <button className="btn-view-ticket">
                      View Conversation
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ Section */}
      {activeTab === 'faq' && (
        <div className="faq-container">
          <div className="faq-grid">
            {FAQS.map((faq, index) => (
              <div key={index} className="faq-card">
                <div className="faq-question" onClick={() => setActiveFaq(activeFaq === index ? null : index)}>
                  <div className="faq-question-content">
                    <HelpCircle size={18} className="faq-icon" />
                    <h6>{faq.q}</h6>
                  </div>
                  <button className="faq-toggle">
                    {activeFaq === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
                {activeFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.a}</p>
                    <div className="faq-category">
                      <span>Category: {faq.category}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="faq-footer">
            <p>Still have questions? <button className="link-btn" onClick={() => setActiveTab('new')}>Contact our support team</button></p>
          </div>
        </div>
      )}

      {/* New Ticket Form */}
      {activeTab === 'new' && (
        <div className="ticket-form-container">
          <div className="form-header">
            <h3>Create New Support Ticket</h3>
            <p>Fill out the form below and our team will get back to you within 24 hours</p>
          </div>
          
          <form onSubmit={handleSubmit} className="ticket-form">
            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label-support">
                  <User size={14} />
                  Subject
                </label>
                <input 
                  type="text" 
                  className="form-control-support" 
                  placeholder="Briefly describe your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label-support">
                  <Tag size={14} />
                  Issue Type
                </label>
                <select 
                  className="form-control-support"
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                >
                  <option value="Payment">💰 Payment & Withdrawals</option>
                  <option value="Order Issue">📦 Order Fulfillment Issue</option>
                  <option value="Account">👤 Account Settings & Profile</option>
                  <option value="Bug">🐛 Technical Bug</option>
                  <option value="Other">❓ Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label-support">
                  <AlertCircle size={14} />
                  Priority
                </label>
                <select className="form-control-support">
                  <option value="low">Low - General inquiry</option>
                  <option value="medium">Medium - Need assistance</option>
                  <option value="high">High - Urgent issue</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label-support">
                  <MessageCircle size={14} />
                  Description
                </label>
                <textarea 
                  className="form-control-support"
                  rows="5"
                  placeholder="Provide detailed information about your issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label-support">
                  <Paperclip size={14} />
                  Attachments (Optional)
                </label>
                <div className="file-upload-area">
                  <input 
                    type="file" 
                    id="file-upload" 
                    className="file-input"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <label htmlFor="file-upload" className="file-upload-label">
                    <Image size={24} />
                    <span>Click to upload screenshot</span>
                    <small>PNG, JPG up to 2MB</small>
                  </label>
                  {selectedFile && (
                    <div className="selected-file">
                      <FileText size={14} />
                      <span>{selectedFile.name}</span>
                      <button type="button" className="remove-file" onClick={() => setSelectedFile(null)}>
                        <XCircle size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel-support" onClick={() => setActiveTab('tickets')}>
                Cancel
              </button>
              <button type="submit" className="btn-submit-support">
                <Send size={16} />
                Submit Ticket
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`.support-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.support-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-support {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-support {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.header-contact {
  display: flex;
  gap: 20px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  color: #475569;
}

.quick-actions-support {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.quick-action {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
}

.quick-action:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.quick-action-icon {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.quick-action-info {
  flex: 1;
}

.quick-action-info h6 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.quick-action-info p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.quick-action-arrow {
  color: #94a3b8;
  transition: all 0.3s ease;
}

.quick-action:hover .quick-action-arrow {
  transform: translateX(4px);
  color: #10b981;
}

.stats-support {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.stats-card-support {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e8ecef;
}

.stats-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
}

.stats-card-support h3 {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.stats-card-support p {
  font-size: 12px;
  color: #64748b;
  margin: 4px 0 0;
}

.support-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  background: white;
  padding: 8px;
  border-radius: 60px;
  width: fit-content;
  border: 1px solid #e8ecef;
}

.tab-support {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: transparent;
  border: none;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-support.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.tickets-container {
  background: white;
  border-radius: 24px;
  padding: 24px;
  border: 1px solid #e8ecef;
}

.tickets-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ticket-card {
  background: #f8fafc;
  border-radius: 20px;
  padding: 20px;
  transition: all 0.3s ease;
}

.ticket-card:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.ticket-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.ticket-id {
  display: flex;
  align-items: center;
  gap: 10px;
}

.id-label {
  font-family: monospace;
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
}

.priority-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.priority-badge.medium {
  background: #fed7aa;
  color: #f59e0b;
}

.priority-badge.low {
  background: #d1fae5;
  color: #10b981;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.open {
  background: #fed7aa;
  color: #f59e0b;
}

.status-badge.progress {
  background: #dbeafe;
  color: #3b82f6;
}

.status-badge.resolved {
  background: #d1fae5;
  color: #10b981;
}

.ticket-subject {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px;
}

.ticket-description {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.5;
}

.ticket-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.btn-view-ticket {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-view-ticket:hover {
  gap: 10px;
}

.faq-container {
  background: white;
  border-radius: 24px;
  padding: 24px;
  border: 1px solid #e8ecef;
}

.faq-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-card {
  background: #f8fafc;
  border-radius: 16px;
  overflow: hidden;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background: #f1f5f9;
}

.faq-question-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.faq-icon {
  color: #10b981;
}

.faq-question h6 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.faq-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
}

.faq-answer {
  padding: 0 20px 20px 52px;
  border-top: 1px solid #e2e8f0;
}

.faq-answer p {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 12px;
}

.faq-category {
  font-size: 11px;
  color: #10b981;
}

.faq-footer {
  text-align: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e8ecef;
}

.link-btn {
  background: none;
  border: none;
  color: #10b981;
  font-weight: 500;
  cursor: pointer;
}

.ticket-form-container {
  background: white;
  border-radius: 24px;
  padding: 28px;
  border: 1px solid #e8ecef;
}

.form-header {
  text-align: center;
  margin-bottom: 28px;
}

.form-header h3 {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
}

.form-header p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.ticket-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group.full-width {
  grid-column: span 2;
}

.form-label-support {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.form-control-support {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-control-support:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.file-upload-area {
  position: relative;
}

.file-input {
  display: none;
}

.file-upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 30px;
  border: 2px dashed #e2e8f0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.file-upload-label:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.selected-file {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 8px;
  font-size: 12px;
}

.remove-file {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 20px;
}

.btn-cancel-support {
  padding: 10px 24px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-submit-support {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 28px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 40px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-submit-support:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-primary-support {
  padding: 10px 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 40px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.empty-state-support {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

@media (max-width: 768px) {
  .support-page {
    padding: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .support-tabs {
    width: 100%;
  }

  .tab-support {
    flex: 1;
    justify-content: center;
  }
}`}</style>
    </div>
  );
};

export default Support;